import React, { useCallback, useEffect, useState } from 'react';

import { Botao, Chip, ErrorState, Modal, Recolhivel, Skeleton } from '../../../ui';
import { detalharLogGeral, LogGeralDetalhe } from '../../../api/log-geral/logGeralApi';
import { formatarDataHora } from '../../../utils/data';
import {
  descreverOrigem,
  descreverQuem,
  ROTULO_TIPO_DE_ATOR,
  rotuloResultado,
  textoDoValor,
} from '../../../utils/logGeralRegras';

/**
 * Detalhe de um registro do log geral (at-85).
 *
 * ⚠️ Abrir o detalhe GERA uma linha no log (`log_geral.detalhar`) — é acesso a dado de
 * terceiros, e quem lê o log também precisa ficar no log.
 */
export interface ModalDetalheLogProps {
  id: string | null;
  aoFechar: () => void;
}

const Par: React.FC<{ rotulo: string; children: React.ReactNode }> = ({ rotulo, children }) => (
  <div className="tw-flex tw-flex-col tw-gap-0.5 sm:tw-flex-row sm:tw-gap-3">
    <dt className="tw-shrink-0 tw-text-xs tw-font-bold tw-text-texto-suave sm:tw-w-40">{rotulo}</dt>
    <dd className="tw-m-0 tw-min-w-0 tw-break-words tw-text-sm tw-text-texto-corpo">{children}</dd>
  </div>
);

const ModalDetalheLog: React.FC<ModalDetalheLogProps> = ({ id, aoFechar }) => {
  const [detalhe, setDetalhe] = useState<LogGeralDetalhe | null>(null);
  const [estado, setEstado] = useState<'carregando' | 'ok' | 'erro'>('carregando');
  const [erro, setErro] = useState('');

  const carregar = useCallback(async () => {
    if (!id) return;
    setEstado('carregando');
    try {
      setDetalhe(await detalharLogGeral(id));
      setEstado('ok');
    } catch (falha) {
      setDetalhe(null);
      setErro(falha instanceof Error ? falha.message : '');
      setEstado('erro');
    }
  }, [id]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const conteudo = () => {
    if (estado === 'carregando') return <Skeleton linhas={6} altura="tw-h-6" />;
    if (estado === 'erro' || !detalhe) {
      return (
        <ErrorState mensagem="Não foi possível abrir este registro." detalhe={erro} aoTentarNovamente={() => void carregar()} />
      );
    }

    const resultado = rotuloResultado(detalhe);
    const mudancas = detalhe.changes ? Object.entries(detalhe.changes) : [];

    return (
      <div className="tw-flex tw-flex-col tw-gap-5">
        <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-2.5">
          <Par rotulo="O que">{detalhe.resumo}</Par>
          <Par rotulo="Resultado">
            <Chip variante={resultado.variante}>{resultado.texto}</Chip>
          </Par>
          <Par rotulo="Quem">{descreverQuem(detalhe)}</Par>
          <Par rotulo="Tipo de ator">{ROTULO_TIPO_DE_ATOR[detalhe.atorTipo] ?? detalhe.atorTipo}</Par>
          <Par rotulo="Registro">
            {detalhe.registro}
            {detalhe.entidadeId && detalhe.entidadeId.length > 12 && (
              <span className="tw-block tw-font-mono tw-text-xs tw-text-texto-suave">{detalhe.entidadeId}</span>
            )}
          </Par>
          <Par rotulo="Origem">{descreverOrigem(detalhe)}</Par>
          <Par rotulo="Código da ação">
            <span className="tw-font-mono tw-text-xs">{detalhe.acao}</span>
          </Par>
          {detalhe.modulo && <Par rotulo="Módulo">{detalhe.modulo}</Par>}
          {detalhe.ip && <Par rotulo="IP">{detalhe.ip}</Par>}
          {detalhe.userAgent && <Par rotulo="Navegador">{detalhe.userAgent}</Par>}
        </dl>

        {mudancas.length > 0 && (
          <section>
            <h3 className="tw-m-0 tw-mb-2 tw-text-sm tw-font-bold tw-text-texto-titulo">Antes e depois</h3>
            <dl className="tw-m-0 tw-flex tw-flex-col tw-gap-2">
              {mudancas.map(([campo, par]) => (
                <Par key={campo} rotulo={campo}>
                  {textoDoValor(par?.antes)} → {textoDoValor(par?.depois)}
                </Par>
              ))}
            </dl>
          </section>
        )}

        {detalhe.linhasDaMesmaRequisicao.length > 0 && (
          <section>
            <h3 className="tw-m-0 tw-mb-2 tw-text-sm tw-font-bold tw-text-texto-titulo">
              O que esta ação gravou ({detalhe.linhasDaMesmaRequisicao.length})
            </h3>
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0">
              {detalhe.linhasDaMesmaRequisicao.map((l) => {
                const r = rotuloResultado(l);
                return (
                  <li
                    key={l.id}
                    className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-superficie-borda tw-p-2.5"
                  >
                    <Chip variante={r.variante}>{r.texto}</Chip>
                    <span className="tw-text-sm tw-text-texto-corpo">{l.resumo}</span>
                    <span className="tw-w-full tw-text-xs tw-text-texto-suave">
                      {l.registro} · {formatarDataHora(l.createdAt)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {detalhe.payload !== null && detalhe.payload !== undefined && (
          <Recolhivel titulo="Dados enviados" resumo="já sem senhas e com CPF e telefone mascarados">
            <pre className="tw-m-0 tw-max-h-72 tw-overflow-auto tw-whitespace-pre-wrap tw-break-words tw-rounded-lg tw-bg-superficie tw-p-3 tw-font-mono tw-text-xs tw-text-texto-corpo">
              {JSON.stringify(detalhe.payload, null, 2)}
            </pre>
          </Recolhivel>
        )}
      </div>
    );
  };

  return (
    <Modal
      aberto={!!id}
      titulo="Registro do log"
      descricao={detalhe ? formatarDataHora(detalhe.createdAt) : undefined}
      tamanho="lg"
      aoFechar={aoFechar}
      alinharRodape="direita"
      rodape={
        <Botao variante="secundario" onClick={aoFechar}>
          Fechar
        </Botao>
      }
    >
      {conteudo()}
    </Modal>
  );
};

export default ModalDetalheLog;
